import { CaseInfoBanner } from "src/components/case-info-banner/case-info-banner";
import { ReceivedLetter, ReceivedLetterOptions } from "src/components/received-letter/received-letter";
import { FieldPath, FormProvider } from "react-hook-form";
import { CaseFormTypes, FormSections, OfferStatus, useCaseFormSetup } from "src/pages/case-landing/case-form-setup";
import { useTranslation } from "react-i18next";
import { Button } from "@ucl/react-library";
import { HowToProceed, HowToProceedOptions } from "src/components/how-to-proceed/how-to-proceed";
import { useNavigate } from "react-router-dom";
import {
  useNoRedressDecisionViewProps
} from "src/pages/case-landing/views/no-redress-decision/no-redress-decision-view-model";
import { ConfirmModal } from "src/components/confirm-modal/confirm-modal";
import { useState } from "react";
import { updateCustomerDetails } from "src/api/services/customer-service";
import { useCase } from "src/context/case-context";
import { Loader } from "src/utils/loader";
import { PortalState } from "src/api/types/enums";


const NoRedressDecisionView = ({ state }: {
  state: PortalState;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);
  const { bannerKey, canChallenge } = useNoRedressDecisionViewProps(state);
  const {customerData, setCustomerData} = useCase();

  const formSections: FormSections[] = ["optStatus", "receivedLetter", "howToProceed"];
  const methods = useCaseFormSetup({ formSections, t });

  if (!customerData) return null;

  const howToProceed = methods.watch("howToProceed");
  const receivedLetter = methods.watch("receivedLetter");

  const hasReceivedLetter = receivedLetter === ReceivedLetterOptions.Received;
  const canContinue = hasReceivedLetter && (!!howToProceed || !canChallenge);

  let continueText = "common.continue";
  if (howToProceed === HowToProceedOptions.ACCEPT) {
    continueText = "landingPage.acceptAndSubmit";
  }
  else if (howToProceed === HowToProceedOptions.CHALLENGE) {
    continueText = "landingPage.continueToChallenge";
  }
  else if (!canChallenge && hasReceivedLetter) {
    continueText = "howToProceed.yesAcceptBtn";
  }

  const handleContinue = async () => {
    const fieldsToValidate: FieldPath<CaseFormTypes>[] = [];

    if (canChallenge) fieldsToValidate.push("howToProceed");

    const valid = fieldsToValidate.length > 0 ? await methods.trigger(fieldsToValidate) : true;
    if (!valid) return;

    if (howToProceed === HowToProceedOptions.CHALLENGE) {
      navigate("/confirm-challenge?step=1");
      return;
    }

    setIsModalOpen(true);
  };

  const handleConfirmAcceptance = async () => {
    try {
      setIsSubmitting(true);
      setShowError(false);
      setIsModalOpen(false);

      const updateData = { offerStatus: OfferStatus.Accept };

      const updatedData = await updateCustomerDetails(updateData);
      setCustomerData(updatedData);
      navigate("/thank-you", {
        state: { pageType: "noRedressAccepted" },
      });
    } catch {
      setIsModalOpen(true);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAcceptance = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <CaseInfoBanner translationKey={`caseInfoBanner.${bannerKey}`} />
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleContinue)} className="contents">
          <ReceivedLetter showComplaint={state === PortalState.ChallengeIntent} />

          {hasReceivedLetter && <HowToProceed canChallenge={canChallenge} />}

          <Button type="button" disabled={!canContinue} onClick={handleContinue} fullWidth={true}>
            {t(continueText)}
          </Button>
        </form>
      </FormProvider>

      <ConfirmModal
        isOpen={isModalOpen}
        handleSubmit={handleConfirmAcceptance}
        handleCancel={handleCancelAcceptance}
        translationKey="confirmAcceptanceModal"
        showError={showError}
      />
      {isSubmitting && <Loader />}
    </>
  );
};

export default NoRedressDecisionView;
